/**
 * WMS Dashboard Mock Data Service
 *
 * Provides simulated warehouse data for the dashboard POC.
 * In production, these would be replaced with real API calls.
 *
 * @author BizClick WMS
 * @version 1.0.0
 */

import {
  OrderSummary,
  PickingStats,
  ReceivingStats,
  InventoryAlert,
  ShippingStats,
  OperatorActivity,
  ActivityEvent,
  KPIData,
  WarehouseCapacity,
  HourlyActivity,
} from './types';

// Helper to add variance to numbers for realism
const withVariance = (base: number, variance: number): number => {
  return Math.round(base + (Math.random() - 0.5) * 2 * variance);
};

// Generate random ID
const generateId = (): string => Math.random().toString(36).substring(2, 11);

/**
 * Get order summary data
 */
export const getOrderSummary = (): OrderSummary => ({
  pending: withVariance(23, 5),
  inProgress: withVariance(15, 3),
  completed: withVariance(142, 20),
  delayed: withVariance(3, 2),
  todayTotal: withVariance(183, 25),
  trend: Math.random() > 0.3 ? 'up' : Math.random() > 0.5 ? 'down' : 'stable',
  trendValue: withVariance(12, 5),
});

/**
 * Get picking statistics
 */
export const getPickingStats = (): PickingStats => ({
  activePickers: withVariance(8, 2),
  ordersInPicking: withVariance(12, 3),
  linesPickedToday: withVariance(1847, 200),
  avgPickTime: withVariance(18, 5),
  pickRate: withVariance(156, 20),
  efficiency: Math.min(100, withVariance(94, 5)),
});

/**
 * Get receiving statistics
 */
export const getReceivingStats = (): ReceivingStats => ({
  expectedDeliveries: withVariance(5, 2),
  receivedToday: withVariance(3, 1),
  pendingPutaway: withVariance(47, 15),
  avgReceiveTime: withVariance(22, 8),
});

/**
 * Get inventory alerts
 */
export const getInventoryAlerts = (): InventoryAlert[] => {
  const alertTypes: InventoryAlert['alertType'][] = ['low_stock', 'out_of_stock', 'overstock', 'expiring'];
  const items = [
    { no: 'SKU-001', name: 'Hydraulikkslange 10mm' },
    { no: 'SKU-042', name: 'Tetningsring 25mm' },
    { no: 'SKU-108', name: 'Bolt M12x50' },
    { no: 'SKU-234', name: 'Filter element HF-200' },
    { no: 'SKU-567', name: 'Aktuator 24V DC' },
    { no: 'SKU-891', name: 'Slitasjedel type A' },
    { no: 'SKU-432', name: 'Koblingsstykke 3/4"' },
  ];

  const numAlerts = withVariance(5, 2);
  const alerts: InventoryAlert[] = [];

  for (let i = 0; i < Math.min(numAlerts, items.length); i++) {
    const item = items[i];
    const alertType = alertTypes[Math.floor(Math.random() * alertTypes.length)];
    alerts.push({
      id: generateId(),
      itemNo: item.no,
      itemName: item.name,
      alertType,
      currentQty: alertType === 'out_of_stock' ? 0 : withVariance(alertType === 'low_stock' ? 5 : 500, 3),
      minQty: alertType === 'low_stock' || alertType === 'out_of_stock' ? 10 : undefined,
      maxQty: alertType === 'overstock' ? 100 : undefined,
      location: `A${withVariance(5, 3)}-${withVariance(3, 2)}-${withVariance(2, 1)}`,
      severity: alertType === 'out_of_stock' || alertType === 'expiring' ? 'critical' : 'warning',
    });
  }

  return alerts;
};

/**
 * Get shipping statistics
 */
export const getShippingStats = (): ShippingStats => ({
  readyToShip: withVariance(28, 8),
  shippedToday: withVariance(89, 15),
  pendingLabels: withVariance(12, 5),
  avgShipTime: withVariance(8, 3),
  onTimeRate: Math.min(100, withVariance(96, 3)),
});

/**
 * Get operator activities
 */
export const getOperatorActivities = (): OperatorActivity[] => {
  const operators = [
    { id: 'Lager1', name: 'Bjørn Hansen' },
    { id: 'Lager2', name: 'Klaus Olsen' },
    { id: 'Lager4', name: 'Aleksander Berg' },
    { id: 'Lager5', name: 'Ilir Berisha' },
    { id: 'Lager6', name: 'Martin Johansen' },
    { id: 'Lager7', name: 'Mads Eriksen' },
    { id: 'Lager8', name: 'Stein Runo' },
    { id: 'Lager9', name: 'Gill Mohammed' },
    { id: 'Lager10', name: 'Kim Andersen' },
    { id: 'Lager11', name: 'Tommy Larsen' },
  ];

  const tasks: { type: OperatorActivity['taskType']; task: string }[] = [
    { type: 'picking', task: 'Plukker ordre #4523' },
    { type: 'picking', task: 'Plukker ordre #4524' },
    { type: 'picking', task: 'Batch-plukk (5 ordrer)' },
    { type: 'receiving', task: 'Mottar leveranse fra Supplier AS' },
    { type: 'receiving', task: 'Mottar retur #R-2341' },
    { type: 'putaway', task: 'Plasserer varer i sone A' },
    { type: 'putaway', task: 'Cross-docking til sone B' },
    { type: 'transfer', task: 'Internflytting til pallplass' },
    { type: 'idle', task: 'Ledig' },
  ];

  // Return random subset of active operators
  const numActive = withVariance(6, 2);
  const shuffled = [...operators].sort(() => Math.random() - 0.5);

  return shuffled.slice(0, numActive).map((op) => {
    const taskInfo = tasks[Math.floor(Math.random() * tasks.length)];
    return {
      operatorId: op.id,
      operatorName: op.name,
      currentTask: taskInfo.task,
      taskType: taskInfo.type,
      performance: Math.min(150, Math.max(50, withVariance(100, 25))),
      linesProcessed: withVariance(180, 60),
    };
  });
};

/**
 * Get recent activity events
 */
export const getRecentActivities = (): ActivityEvent[] => {
  const types: ActivityEvent['type'][] = ['pick', 'receive', 'putaway', 'transfer', 'adjustment'];
  const operators = ['Bjørn', 'Klaus', 'Martin', 'Mads', 'Kim', 'Tommy', 'Ilir'];
  const locations = ['A1-2-3', 'B2-1-4', 'C3-4-1', 'D1-1-2', 'A4-3-2', 'B1-2-1', 'C2-3-4'];
  const items = ['SKU-001', 'SKU-042', 'SKU-108', 'SKU-234', 'SKU-567', 'SKU-891'];

  const descriptions: Record<ActivityEvent['type'], string[]> = {
    pick: ['Plukket til ordre', 'Batch-plukk fullført', 'Delplukk utført'],
    receive: ['Vare mottatt', 'Leveranse registrert', 'Retur mottatt'],
    putaway: ['Vare plassert', 'Plassering fullført', 'Cross-dock utført'],
    transfer: ['Vare flyttet', 'Intern overføring', 'Pallflytting'],
    adjustment: ['Beholdning justert', 'Korreksjon utført', 'Telling oppdatert'],
  };

  const events: ActivityEvent[] = [];
  const now = new Date();

  for (let i = 0; i < 15; i++) {
    const type = types[Math.floor(Math.random() * types.length)];
    const timestamp = new Date(now.getTime() - i * withVariance(180000, 60000)); // ~3 min apart

    events.push({
      id: generateId(),
      timestamp,
      type,
      operator: operators[Math.floor(Math.random() * operators.length)],
      location: locations[Math.floor(Math.random() * locations.length)],
      itemNo: items[Math.floor(Math.random() * items.length)],
      qty: withVariance(5, 3),
      description: descriptions[type][Math.floor(Math.random() * descriptions[type].length)],
    });
  }

  return events.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
};

/**
 * Get KPI data
 */
export const getKPIs = (): KPIData[] => [
  {
    name: 'Plukkeffektivitet',
    value: Math.min(100, withVariance(94, 4)),
    target: 95,
    unit: '%',
    trend: Math.random() > 0.5 ? 'up' : 'stable',
    trendValue: withVariance(2, 1),
  },
  {
    name: 'Ordre levert i tide',
    value: Math.min(100, withVariance(97, 2)),
    target: 98,
    unit: '%',
    trend: Math.random() > 0.3 ? 'up' : 'down',
    trendValue: withVariance(1, 0.5),
  },
  {
    name: 'Plukkfeil',
    value: Math.max(0, withVariance(0.8, 0.3)),
    target: 0.5,
    unit: '%',
    trend: Math.random() > 0.5 ? 'down' : 'stable',
    trendValue: withVariance(0.1, 0.05),
  },
  {
    name: 'Lagersnurr',
    value: withVariance(12, 2),
    target: 15,
    unit: 'x/år',
    trend: 'up',
    trendValue: withVariance(0.5, 0.2),
  },
  {
    name: 'Linjer per time',
    value: withVariance(156, 20),
    target: 150,
    unit: 'linjer',
    trend: Math.random() > 0.4 ? 'up' : 'stable',
    trendValue: withVariance(8, 3),
  },
  {
    name: 'Mottak til hylle',
    value: withVariance(45, 10),
    target: 30,
    unit: 'min',
    trend: Math.random() > 0.6 ? 'down' : 'stable',
    trendValue: withVariance(3, 1),
  },
];

/**
 * Get warehouse capacity
 */
export const getWarehouseCapacity = (): WarehouseCapacity => {
  const zones = [
    { name: 'Sone A - Paller', total: 450, base: 380 },
    { name: 'Sone B - Småvarer', total: 1200, base: 920 },
    { name: 'Sone C - Kjøl', total: 80, base: 65 },
    { name: 'Sone D - Bulk', total: 120, base: 95 },
  ];

  const zoneData = zones.map((z) => {
    const used = withVariance(z.base, z.total * 0.05);
    return {
      name: z.name,
      total: z.total,
      used: Math.min(z.total, Math.max(0, used)),
      percent: Math.round((Math.min(z.total, used) / z.total) * 100),
    };
  });

  const totalLocations = zoneData.reduce((sum, z) => sum + z.total, 0);
  const usedLocations = zoneData.reduce((sum, z) => sum + z.used, 0);

  return {
    totalLocations,
    usedLocations,
    utilizationPercent: Math.round((usedLocations / totalLocations) * 100),
    zones: zoneData,
  };
};

/**
 * Get hourly activity data for charts
 */
export const getHourlyActivity = (): HourlyActivity[] => {
  const now = new Date();
  const currentHour = now.getHours();
  const data: HourlyActivity[] = [];

  for (let i = 0; i < 12; i++) {
    const hour = (currentHour - 11 + i + 24) % 24;
    const hourStr = `${hour.toString().padStart(2, '0')}:00`;

    // More activity during work hours
    const isWorkHour = hour >= 6 && hour <= 18;
    const multiplier = isWorkHour ? (hour >= 8 && hour <= 16 ? 1 : 0.6) : 0.1;

    data.push({
      hour: hourStr,
      picks: Math.round(withVariance(50, 15) * multiplier),
      receives: Math.round(withVariance(15, 5) * multiplier),
      shipments: Math.round(withVariance(25, 8) * multiplier),
    });
  }

  return data;
};

/**
 * Get current date/time info
 */
export const getCurrentDateTime = () => {
  const now = new Date();
  return {
    date: now.toLocaleDateString('nb-NO', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }),
    time: now.toLocaleTimeString('nb-NO', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    }),
    week: `Uke ${getWeekNumber(now)}`,
  };
};

// Helper to get week number
function getWeekNumber(date: Date): number {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
}
